/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'

import { DmeOfficeOption, useDmeOfficeOptions } from 'hooks/useDmeOfficeOptions'
import { useGetOfficeSettingsQuery } from 'store/services/office'
import { useSetPatientPreferredDmeMutation } from 'store/services/patient'

interface IProps {
  patientId: number
  officeId?: number | string | null
  preferredDmeOfficeId?: number | null
}

// The DME the patient chose, kept on the patient so every order uses it. When a
// signed PAP Rx comes back on a client office's order, the referral packet is
// faxed here, or to the office's preferred DME when nothing is chosen
// (TES-3159). It saves on its own, apart from the patient form.
export const PatientPreferredDme = ({ patientId, officeId, preferredDmeOfficeId }: IProps) => {
  const { options, isLoading: isLoadingOffices } = useDmeOfficeOptions()
  const { data: officeSettings } = useGetOfficeSettingsQuery({ id: Number(officeId) }, { skip: !officeId })
  const [setPreferredDme, { isLoading: isSaving }] = useSetPatientPreferredDmeMutation()
  const [menuOpen, setMenuOpen] = useState(false)

  const selected = options.find((option) => option.value === Number(preferredDmeOfficeId)) ?? null
  const officeDefault = officeSettings?.isPreferredDmeNone
    ? null
    : options.find((option) => option.value === Number(officeSettings?.preferredDmeOfficeId))

  const handleChange = async (option: DmeOfficeOption | null) => {
    try {
      await setPreferredDme({ patientId, preferredDmeOfficeId: option?.value ?? null }).unwrap()
      toast.success(option ? 'Preferred DME updated.' : 'Preferred DME cleared.')
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update the preferred DME.')
    }
  }

  return (
    <div className="form-group textGray">
      <label htmlFor="patientPreferredDme">Preferred DME</label>
      <ReactSelect
        inputId="patientPreferredDme"
        className="basic-single"
        classNamePrefix="select"
        isClearable
        isSearchable
        isLoading={isLoadingOffices || isSaving}
        isDisabled={isSaving}
        placeholder="Search DME offices..."
        value={selected}
        onChange={(option) => handleChange(option as DmeOfficeOption | null)}
        // It sits inside the patient form; Enter on a closed menu must not
        // submit the whole form.
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !menuOpen) {
            event.preventDefault()
          }
        }}
        options={options}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
      />
      <small className="text-muted d-block mt-1" data-testid="preferred-dme-hint">
        {selected && 'Saved as soon as it is picked. A signed PAP Rx on a client office\'s order is faxed to this DME.'}
        {!selected && officeDefault
          && `None chosen: the office's preferred DME, ${officeDefault.label}, is used.`}
        {!selected && !officeDefault && 'None chosen, and the office has no preferred DME.'}
      </small>
    </div>
  )
}
